import { formatISO } from 'date-fns-jalali/formatISO';
import { v4 as uuidv4 } from 'uuid';

import type {
  AnswerChild,
  CommentChild,
  CommentParent,
  FeedbackShare,
  FeedbackTopicType,
  FeedbackUser,
  QuestionParent,
  ReviewParent,
} from '../../types/feedbacks';

type FeedbackArgs = {
  content: FeedbackShare['content'];
  ranges: FeedbackShare['ranges'];
  user: FeedbackUser;
  topicType: FeedbackTopicType;
};

type ReviewFeedbackArgs = {
  score: number;
} & FeedbackArgs;

type FeedbackBase = {
  content: FeedbackShare['content'];
  ranges: FeedbackShare['ranges'];
  user: FeedbackUser;
  topicType: FeedbackTopicType;
};

const feedbackBase = ({ content, user, topicType, ranges }: FeedbackBase): FeedbackShare => {
  const fakeUserId = uuidv4();
  return {
    id: uuidv4(),
    userId: fakeUserId,
    topicType: topicType,
    topicId: '',
    topicName: '',
    tenant: '',
    trashReasons: [],
    mentions: [],
    content,
    ranges,
    childrenCount: 0,
    likesCount: 0,
    status: ['None'],
    isLikedByCurrentUser: false,
    user: {
      userId: user.userId || fakeUserId,
      fullName: user.fullName,
      avatarId: user.avatarId,
      displayFullName: user.displayFullName,
    },
    createdAt: formatISO(new Date(), { representation: 'complete' }),
  };
};

export const createClientAnswer = ({ content, user, topicType, ranges }: FeedbackBase): AnswerChild => ({
  ...feedbackBase({ content, user, topicType, ranges }),
  questionId: '',
  type: 'Answer',
});

export const createClientCommentChild = ({
  content,
  user,
  topicType,
  ranges,
}: FeedbackBase): CommentChild => ({
  ...feedbackBase({ content, user, topicType, ranges }),
  parentRootId: '',
  parentId: '',
  type: 'CommentChild',
  commentChildren: [],
});

export const createClientQuestion = ({ content, user, topicType, ranges }: FeedbackArgs): QuestionParent => ({
  ...feedbackBase({ content, user, topicType, ranges }),
  type: 'Question',
  answers: [],
  bestAnswerId: null,
});

export const createClientComment = ({ content, user, topicType, ranges }: FeedbackArgs): CommentParent => ({
  ...feedbackBase({ content, user, topicType, ranges }),
  type: 'Comment',
  commentChildren: [],
});

export const createClientReview = ({
  content,
  user,
  topicType,
  score,
  ranges,
}: ReviewFeedbackArgs): ReviewParent => ({
  ...feedbackBase({ content, user, topicType, ranges }),
  type: 'Review',
  commentChildren: [],
  score,
});
